import { EditorExtensionEnum, extensionsForSms, TipTapEditor } from '@systemeio/tip-tap';
import { useRevalidateModules } from '@systemeio/util';
import { SmsTemplateRadioGroupSenderType } from 'modules/sms/components/sms-template-radio-group-sender-type';
import { defaultSmsTemplateValues } from 'modules/sms/constants/default-sms-template-values';
import React, { useEffect } from 'react';
import { FieldErrorAndDescription, FieldLabel, FormInput, toast } from 'shared/components';
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component';
import { CARD_FRAME_CLASSNAME } from 'shared/constants/card-class-names';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { deepCompare } from 'shared/utils/deepCompare';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { twJoin } from 'tailwind-merge';
import { useSmsSubstitutes } from '../hooks/use-sms-substitutes';
import { useSmsTemplateActions } from '../hooks/use-sms-template-actions';
import { SmsTemplateCreateInterface, SmsTemplateId, SmsTemplateUpdateInterface } from '../types';
import { SmsTemplateSettingsPanel } from './sms-template-settings-panel';
const defaultErrors = {
  name: '',
  content: '',
  phoneNumber: '',
  alphanumericSender: '',
  messageServiceSid: ''
};
export interface SmsTemplateFormProps {
  templateId?: SmsTemplateId;
  data?: SmsTemplateCreateInterface;
  disabled?: boolean;
  onStatusChange?: ({
    isFetching,
    hasTwilioErrors
  }: {
    isFetching: boolean;
    hasTwilioErrors: boolean;
  }) => void;
  onSuccess?: (_: SmsTemplateUpdateInterface) => Promise<void>;
}
SmsTemplateForm.formId = 'sms-template-form';
export function SmsTemplateForm({
  templateId,
  data,
  disabled,
  onStatusChange,
  onSuccess
}: SmsTemplateFormProps) {
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [tempState, setTempState] = React.useState<SmsTemplateCreateInterface>(defaultSmsTemplateValues);
  const [errors, setErrors] = React.useState(defaultErrors);
  const [hasTwilioErrors, setHasTwilioErrors] = React.useState<boolean>(false);
  const {
    t
  } = useLocoTranslation();
  const {
    createSmsTemplate,
    updateSmsTemplate
  } = useSmsTemplateActions();
  const {
    revalidateModules
  } = useRevalidateModules();
  useEffect(() => {
    if (data) {
      setTempState({
        name: data.name,
        phoneNumber: data.phoneNumber,
        content: data.content,
        senderType: data.senderType,
        alphanumericSender: data.alphanumericSender,
        messageServiceSid: data.messageServiceSid
      });
    }
  }, [data]);
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({
        isFetching,
        hasTwilioErrors
      });
    }
  }, [isFetching, hasTwilioErrors]);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFetching) return;
    if (isPreFetching) return;
    if (disabled) return;
    setIsFetching(true);
    setErrors(defaultErrors);
    try {
      let response: SmsTemplateUpdateInterface;
      if (templateId) {
        response = await updateSmsTemplate({
          id: templateId,
          ...tempState
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_updated'));
      } else {
        response = await createSmsTemplate({
          ...tempState
        });
        await revalidateModules([RequestModuleEnum.SmsTemplates]);
        await onSuccess?.(response);
        toast.success(t('dashboard.sms.sms_template_successfully_created'));
      }
    } catch (error) {
      handle422Error(error, setErrors);
    } finally {
      setIsFetching(false);
    }
  };
  const isDataBeingChanged = React.useMemo(() => {
    if (!templateId) {
      return !deepCompare(defaultSmsTemplateValues, tempState);
    }
    const dataToCompare: SmsTemplateCreateInterface | null = data ? {
      name: data.name,
      senderType: data.senderType,
      phoneNumber: data.phoneNumber,
      alphanumericSender: data.alphanumericSender,
      messageServiceSid: data.messageServiceSid,
      content: data.content
    } : null;
    return !deepCompare(dataToCompare, tempState);
  }, [templateId, data, tempState]);
  const {
    substitutionData
  } = useSmsSubstitutes();
  const isPreFetching = templateId ? !data : false;
  return <form id={SmsTemplateForm.formId} className="desktop:grid-cols-[minmax(0,1fr)_400px] grid items-start gap-4"
  // Hidden tab panels stay mounted, so native validation would block submit on a field it can't
  // focus. The server's 422 drives field errors and tab flags instead.
  noValidate onSubmit={handleSubmit} data-test="sms-template-form">
      <div className="flex min-w-0 flex-col gap-2">
        <div className={twJoin(CARD_FRAME_CLASSNAME, 'p-5')} data-test="sms-template-naming">
          <FormInput label={t('dashboard.sms.template_name')} value={tempState.name} isPreFetching={isPreFetching} disabled={disabled || isFetching} onChange={e_0 => setTempState({
          ...tempState,
          name: e_0.target.value
        })} required error={errors.name} />
        </div>

        <div className={twJoin(CARD_FRAME_CLASSNAME, 'flex flex-col gap-2 p-5')} data-test="sms-template-content">
          <FieldLabel label={t('dashboard.sms.message_text')} />
          <TipTapEditor mode="text" className={twJoin(errors.content && 'border-field-border-error')} extensions={extensionsForSms} isPreFetching={isPreFetching} isEditable={!disabled && !isFetching} content={tempState.content} onChange={content => setTempState({
          ...tempState,
          content
        })} extensionOptions={{
          [EditorExtensionEnum.Substitution]: {
            substitutions: substitutionData
          }
        }} characterCountHint={t('dashboard.simple_mail_editor.character_count.tooltip')} />
          <FieldErrorAndDescription error={errors.content} />
        </div>
      </div>

      <SmsTemplateSettingsPanel hasSenderError={Boolean(hasTwilioErrors || errors.phoneNumber || errors.alphanumericSender || errors.messageServiceSid)} sender={<SmsTemplateRadioGroupSenderType hideLabel withTwilioAlert errors={errors} isFetching={isFetching} isPreFetching={isPreFetching} values={{
      senderType: tempState.senderType,
      phoneNumber: tempState.phoneNumber,
      alphanumericSender: tempState.alphanumericSender,
      messageServiceSid: tempState.messageServiceSid
    }} onChange={updates => setTempState(prev => ({
      ...prev,
      ...updates
    }))} disabled={disabled} onTwilioHasErrors={setHasTwilioErrors} />} />

      <ConfirmationModalBeforeExit confirmationText={t('dashboard.sms.confirmation_message_before_exit')} hideModal={!isDataBeingChanged || isFetching} />
    </form>;
}